import { Check, ChevronDown } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  DemandReadiness,
  DemandReadinessModal,
} from '@/components/matter-form/DemandReadinessModal';
import { MatterStatusNoteModal } from '@/components/matter-form/MatterStatusNoteModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  usePatchMatterMutation,
  useSaveDemandReadinessMutation,
} from '@/services/api/mattersService';
import { useCreateStaffNoteMutation } from '@/services/api/staffNotesService';
import { MatterStatus } from '@/services/types/client-intake-types';

import {
  getStatusBgColorHover,
  getStatusTextColor,
  isTerminalMatterStatus,
  USER_SELECTABLE_MATTER_STATUSES,
} from '../../matters/config/matterStatusConfig';

// Status values are already display-formatted (e.g., "Initial Review")
const getStatusLabel = (status: MatterStatus): string => status;

interface MatterStatusBadgeProps {
  matterId: string;
  matterName: string;
  currentStatus: MatterStatus;
  className?: string;
}

export const MatterStatusBadge: React.FC<MatterStatusBadgeProps> = ({
  matterId,
  matterName,
  currentStatus,
  className,
}) => {
  const [patchMatter, { isLoading }] = usePatchMatterMutation();
  const [createStaffNote] = useCreateStaffNoteMutation();
  const [saveDemandReadiness] = useSaveDemandReadinessMutation();

  // State for terminal status note modal
  const [statusNoteModalOpen, setStatusNoteModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<MatterStatus | null>(null);

  // State for demand readiness modal
  const [demandReadinessModalOpen, setDemandReadinessModalOpen] =
    useState(false);

  // Check if changing to a terminal status that requires a note
  const isChangingToTerminalStatus = (newStatus: MatterStatus): boolean => {
    return isTerminalMatterStatus(newStatus) && newStatus !== currentStatus;
  };

  // Check if changing to Ready for Demand status
  const isChangingToReadyForDemand = (newStatus: MatterStatus): boolean => {
    return (
      newStatus === MatterStatus.READY_FOR_DEMAND && newStatus !== currentStatus
    );
  };

  // Perform the actual status update
  const performStatusUpdate = async (newStatus: MatterStatus) => {
    await patchMatter({
      matter_id: matterId,
      patch: { status: newStatus },
    }).unwrap();

    toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
  };

  const handleStatusChange = async (newStatus: MatterStatus) => {
    if (newStatus === currentStatus) {
      return;
    }

    // If changing to Ready for Demand, show demand readiness modal
    if (isChangingToReadyForDemand(newStatus)) {
      setPendingStatus(newStatus);
      setDemandReadinessModalOpen(true);
      return;
    }

    // If changing to a terminal status, show modal to require a note
    if (isChangingToTerminalStatus(newStatus)) {
      setPendingStatus(newStatus);
      setStatusNoteModalOpen(true);
      return;
    }

    // Otherwise, update directly
    try {
      await performStatusUpdate(newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Handle modal confirmation - create staff note then update status
  const handleStatusNoteConfirm = async (note: string) => {
    if (!pendingStatus) return;

    try {
      // First, create the staff note explaining the status change
      await createStaffNote({
        matterId,
        note: { content: note },
      }).unwrap();

      // Then perform the actual status update
      await performStatusUpdate(pendingStatus);

      // Close modal and reset state
      setStatusNoteModalOpen(false);
      setPendingStatus(null);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
      throw error; // Re-throw to let modal know submission failed
    }
  };

  // Handle modal close
  const handleStatusNoteModalClose = () => {
    setStatusNoteModalOpen(false);
    setPendingStatus(null);
  };

  // Handle demand readiness modal confirmation
  const handleDemandReadinessConfirm = async (data: DemandReadiness) => {
    if (!pendingStatus) return;

    try {
      // Save demand readiness information
      await saveDemandReadiness({
        matterId,
        demandReadiness: data,
      }).unwrap();

      // Then perform the actual status update
      await performStatusUpdate(pendingStatus);

      // Close modal and reset state
      setDemandReadinessModalOpen(false);
      setPendingStatus(null);
    } catch (error) {
      console.error('Failed to save demand readiness:', error);
      toast.error('Failed to save demand readiness information');
      throw error; // Re-throw to let modal know submission failed
    }
  };

  // Handle demand readiness modal close
  const handleDemandReadinessModalClose = () => {
    setDemandReadinessModalOpen(false);
    setPendingStatus(null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isLoading}
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
            'bg-zinc-50 hover:bg-zinc-100 data-[state=open]:bg-zinc-100',
            isLoading && 'opacity-50 cursor-not-allowed',
            className,
          )}
          aria-label={`Change status from ${getStatusLabel(currentStatus)}`}
        >
          <span className="text-zinc-500">Status:</span>
          <span
            className={cn('font-medium', getStatusTextColor(currentStatus))}
          >
            {getStatusLabel(currentStatus)}
          </span>
          <ChevronDown
            className={cn(
              'h-3 w-3 text-zinc-400 transition-transform duration-200',
            )}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <div className="max-h-[300px] overflow-y-auto">
          {USER_SELECTABLE_MATTER_STATUSES.map((status) => {
            const isSelected = status === currentStatus;
            const colorClass = getStatusBgColorHover(status);

            return (
              <DropdownMenuItem
                key={status}
                onSelect={() => handleStatusChange(status)}
                disabled={isLoading}
                className={cn(
                  'flex items-center justify-between px-2 py-2 text-xs cursor-pointer',
                  isSelected && 'bg-zinc-50',
                )}
              >
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-md font-medium',
                    colorClass,
                  )}
                >
                  {getStatusLabel(status)}
                </span>
                {isSelected && (
                  <Check className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>

      {/* Modal for requiring note when changing to terminal status */}
      {pendingStatus && statusNoteModalOpen && (
        <MatterStatusNoteModal
          isOpen={statusNoteModalOpen}
          onClose={handleStatusNoteModalClose}
          onConfirm={handleStatusNoteConfirm}
          targetStatus={pendingStatus}
          matterName={matterName}
        />
      )}

      {/* Modal for demand readiness when transitioning to Ready for Demand */}
      {pendingStatus && demandReadinessModalOpen && (
        <DemandReadinessModal
          isOpen={demandReadinessModalOpen}
          onClose={handleDemandReadinessModalClose}
          onConfirm={handleDemandReadinessConfirm}
          matterName={matterName}
        />
      )}
    </DropdownMenu>
  );
};
